import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AuditEntry, AuditTrailFilters } from "@/api/admin-api"

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  RefreshCw,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { cn } from "@/lib/utils"

import { AuditTableRow, AuditTableSkeleton } from "./audit-trail-components"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { useAuditTrailQuery } from "../hooks/admin-queries"

const RESOURCE_TYPES = [
  "library",
  "model",
  "route",
  "feature",
  "tool",
  "mcp",
  "tool_action",
  "embedding",
  "specialized",
] as const

// Action types for filtering (last part of action strings like "library.config.update")
const ACTION_TYPES = ["update", "create", "delete", "clear"] as const

const PAGE_SIZE = 20

type ExpandedRowState = Record<string, boolean>

function AuditTableContent({
  entries,
  expandedRows,
  toggleRow,
}: {
  entries: AuditEntry[]
  expandedRows: ExpandedRowState
  toggleRow: (id: string) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="overflow-hidden overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead className="w-44 px-4">
              {t("adminDashboard.auditTrail.columns.timestamp")}
            </TableHead>
            <TableHead className="w-52 px-4">
              {t("adminDashboard.auditTrail.columns.user")}
            </TableHead>
            <TableHead className="w-52 px-4">
              {t("adminDashboard.auditTrail.columns.action")}
            </TableHead>
            <TableHead className="px-4">
              {t("adminDashboard.auditTrail.columns.resource")}
            </TableHead>
            <TableHead className="w-12 px-2" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <AuditTableRow
              key={entry.id}
              entry={entry}
              isExpanded={expandedRows[entry.id] ?? false}
              onToggle={() => toggleRow(entry.id)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Clock className="text-muted-foreground/50 mb-3 size-10" />
      <p className="text-muted-foreground font-medium">{t("adminDashboard.auditTrail.empty")}</p>
      <p className="text-muted-foreground mt-1 text-sm">
        {t("adminDashboard.auditTrail.emptyDescription")}
      </p>
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-destructive">{t("adminDashboard.auditTrail.error")}</p>
      <Button className="mt-4" size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}

export function AuditTrailSection() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<AuditTrailFilters>({
    limit: PAGE_SIZE,
    offset: 0,
  })
  const [expandedRows, setExpandedRows] = useState<ExpandedRowState>({})
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, isError, refetch, isFetching } = useAuditTrailQuery(filters)

  const uniqueUsers = useMemo(() => {
    if (!data?.entries) return []
    const users = new Map<string, string>()
    for (const entry of data.entries) {
      if (entry.user_id && !users.has(entry.user_id)) {
        users.set(entry.user_id, entry.user_email ?? entry.user_id)
      }
    }
    return Array.from(users.entries())
  }, [data?.entries])

  const handleFilterChange = (key: keyof AuditTrailFilters, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
      offset: 0,
    }))
  }

  const handlePageChange = (direction: "prev" | "next") => {
    const currentOffset = filters.offset ?? 0
    const newOffset =
      direction === "prev" ? Math.max(0, currentOffset - PAGE_SIZE) : currentOffset + PAGE_SIZE
    setFilters((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleReset = () => {
    setFilters({ limit: PAGE_SIZE, offset: 0 })
    setExpandedRows({})
  }

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const currentOffset = filters.offset ?? 0
  const total = data?.total ?? 0
  const from = total > 0 ? currentOffset + 1 : 0
  const to = Math.min(currentOffset + PAGE_SIZE, total)
  const hasNextPage = currentOffset + PAGE_SIZE < total
  const hasPrevPage = currentOffset > 0

  const renderContent = () => {
    if (isLoading) {
      return <AuditTableSkeleton />
    }
    if (isError) {
      return <ErrorState onRetry={() => void refetch()} />
    }
    if (!data?.entries.length) {
      return <EmptyState />
    }
    return (
      <>
        <AuditTableContent
          entries={data.entries}
          expandedRows={expandedRows}
          toggleRow={toggleRow}
        />
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t("adminDashboard.auditTrail.pagination.showing", { from, to, total })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              className="h-8"
              disabled={!hasPrevPage || isFetching}
              size="sm"
              variant="outline"
              onClick={() => handlePageChange("prev")}
            >
              <ChevronLeft className="mr-1 size-4" />
              {t("adminDashboard.auditTrail.pagination.previous")}
            </Button>
            <Button
              className="h-8"
              disabled={!hasNextPage || isFetching}
              size="sm"
              variant="outline"
              onClick={() => handlePageChange("next")}
            >
              {t("adminDashboard.auditTrail.pagination.next")}
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <CollapsibleManagementSection
      defaultExpanded={false}
      icon={<FileText className="size-5" />}
      id="audit-trail"
      storageKey="admin-audit-trail-open"
      title={t("adminDashboard.auditTrail.title")}
      headerActions={
        <Button
          className="h-8 gap-1.5"
          disabled={isFetching}
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            void refetch()
          }}
        >
          <RefreshCw className={cn("size-3.5", isFetching && "animate-spin")} />
          <span className="sr-only">Refresh</span>
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button className="gap-2" size="sm" variant="outline">
                <Filter className="size-3.5" />
                {t("adminDashboard.auditTrail.filters.apply")}
                <ChevronDown
                  className={cn("size-3.5 transition-transform", showFilters && "rotate-180")}
                />
              </Button>
            </CollapsibleTrigger>
            {(filters.resource_type || filters.action_type || filters.user_id) && (
              <Button size="sm" variant="ghost" onClick={handleReset}>
                {t("adminDashboard.auditTrail.filters.reset")}
              </Button>
            )}
          </div>
          <CollapsibleContent className="pt-3">
            <div className="flex flex-wrap gap-3">
              <div className="w-48">
                <Select
                  value={filters.resource_type ?? "all"}
                  onValueChange={(v) => handleFilterChange("resource_type", v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue
                      placeholder={t("adminDashboard.auditTrail.filters.resourceType")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("adminDashboard.auditTrail.filters.allResourceTypes")}
                    </SelectItem>
                    {RESOURCE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`adminDashboard.auditTrail.resourceTypes.${type}`, type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Select
                  value={filters.action_type ?? "all"}
                  onValueChange={(v) => handleFilterChange("action_type", v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t("adminDashboard.auditTrail.filters.action")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("adminDashboard.auditTrail.filters.allActions")}
                    </SelectItem>
                    {ACTION_TYPES.map((actionType) => (
                      <SelectItem key={actionType} value={actionType}>
                        {t(
                          `adminDashboard.auditTrail.actionTypes.${actionType}`,
                          actionType.charAt(0).toUpperCase() + actionType.slice(1)
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-56">
                <Select
                  value={filters.user_id ?? "all"}
                  onValueChange={(v) => handleFilterChange("user_id", v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={t("adminDashboard.auditTrail.filters.user")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("adminDashboard.auditTrail.filters.allUsers")}
                    </SelectItem>
                    {uniqueUsers.map(([id, email]) => (
                      <SelectItem key={id} value={id}>
                        {email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {renderContent()}
      </div>
    </CollapsibleManagementSection>
  )
}
